import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import DataTable from "components/ui/Tables/DataTable";
import { request } from "app/network";
import { useMaterialUIController } from "context";
import BodyPartModal from "./BodyPartModal";

function BodyPartListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedBodyPart, setSelectedBodyPart] = useState<any | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bodyPartToDelete, setBodyPartToDelete] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ["BodyPart.pagedList", searchTerm],
        queryFn: async () => {
            const response = await request("BodyPart.pagedList", {
                paramz: {
                    PageNumber: 1,
                    PageSize: 1000,
                    Name: searchTerm,
                },
            });
            return response.data;
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (seqId: number) => {
            await request("BodyPart.delete", {
                pathParams: { seqId: seqId.toString() },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["BodyPart.pagedList"] });
            setDeleteDialogOpen(false);
            setBodyPartToDelete(null);
        },
        onError: (error: any) => {
            alert(error?.response?.data?.message || "삭제에 실패했습니다.");
        }
    });

    const handleCreate = () => {
        setSelectedBodyPart(null);
        setModalOpen(true);
    };

    const handleEdit = (bodyPart: any) => {
        setSelectedBodyPart(bodyPart);
        setModalOpen(true);
    };

    const handleDeleteClick = (bodyPart: any) => {
        setBodyPartToDelete(bodyPart);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (bodyPartToDelete) {
            deleteMutation.mutate(bodyPartToDelete.seqId);
        }
    };

    const handleModalSuccess = () => {
        setModalOpen(false);
        setSelectedBodyPart(null);
        queryClient.invalidateQueries({ queryKey: ["BodyPart.pagedList"] });
    };

    const columns = [
        { Header: "SEQ", accessor: "seqId", width: "5%" },
        { Header: "카테고리", accessor: "categoryName", width: "10%" },
        { Header: "코드", accessor: "code", width: "10%" },
        { Header: "이름", accessor: "name", width: "20%" },
        { Header: "설명", accessor: "description", width: "25%" },
        { Header: "순서", accessor: "displayOrder", width: "5%", align: "center" },
        {
            Header: "활성",
            accessor: "isActive",
            width: "10%",
            align: "center",
            Cell: ({ value }: any) => (
                <Chip
                    label={value ? "Active" : "Inactive"}
                    color={value ? "success" : "default"}
                    size="small"
                />
            )
        },
        {
            Header: "관리",
            accessor: "actions",
            width: "10%",
            align: "center",
            Cell: ({ row }: any) => (
                <MDBox display="flex" alignItems="center" justifyContent="center">
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(row.original)}
                        sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                            '&:hover': {
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                color: '#49a3f1',
                            },
                        }}
                    >
                        <Icon fontSize="small">edit</Icon>
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(row.original)}
                        sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                            '&:hover': {
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                color: '#f44336',
                            },
                        }}
                    >
                        <Icon fontSize="small">delete</Icon>
                    </IconButton>
                </MDBox>
            ),
        },
    ];

    const rows = (pagedData as any)?.items || [];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">accessibility_new</Icon>
                                    <MDTypography variant="h5" fontWeight="medium" ml={1}>
                                        부위 목록
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={2}>
                                    <MDInput
                                        label="이름 검색"
                                        value={searchTerm}
                                        onChange={(e: any) => setSearchTerm(e.target.value)}
                                    />
                                    <MDButton variant="gradient" color="info" onClick={handleCreate}>
                                        <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
                                        관심부위 등록
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text">
                                관심부위(Body Part) 목록 조회 및 관리
                            </MDTypography>
                        </MDBox>

                        <DataTable
                            table={{ columns, rows }}
                            showTotalEntries={true}
                            isSorted={false}
                            noEndBorder
                            entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                        />
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />

            <BodyPartModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={handleModalSuccess}
                bodyPart={selectedBodyPart}
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>관심부위 삭제</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        정말로 삭제하시겠습니까?
                        <br />
                        <strong>{bodyPartToDelete?.name}</strong> ({bodyPartToDelete?.code})
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} variant="outlined" color="dark">
                        취소
                    </MDButton>
                    <MDButton onClick={confirmDelete} variant="gradient" color="error">
                        삭제
                    </MDButton>
                </DialogActions>
            </Dialog>

        </DashboardLayout>
    );
}

export default BodyPartListScreen;
